// frontend/src/hooks/usePlanUsage.ts
import { useEffect, useState, useCallback } from 'react';
import { useAuthStore } from '../store/authStore';
import { authService } from '../services/auth.service';

const API_BASE = (import.meta.env.PROD && (import.meta.env.VITE_API_BASE_URL === 'http://localhost:8080' || !import.meta.env.VITE_API_BASE_URL)) ? '' : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080');

export interface PlanUsage {
  lessonsUsed: number;
  lessonsLimit: number | null;
  aiMessagesUsed: number;
  aiMessagesLimit: number | null;
  projectsUsed: number;
  projectsLimit: number | null;
  codeEvaluationsUsed?: number;
  codeEvaluationsLimit?: number | null;
  gamesUsed?: number;
  gamesLimit?: number | null;
  quizzesGeneratedUsed?: number;
  quizzesGeneratedLimit?: number | null;
  quizzesSolvedUsed?: number;
  quizzesSolvedLimit?: number | null;
  planName: string;
  planTier: string;
}

export type LimitType = 'lesson' | 'ai_message' | 'project' | 'code_evaluation' | 'game_play' | 'quiz_generation' | 'quiz_solve';

// Global caching/sharing to avoid redundant and looped requests
let activePromise: Promise<PlanUsage> | null = null;
let cachedUsage: PlanUsage | null = null;
let cacheTimestamp = 0;
const CACHE_DURATION_MS = 30_000; // 30 seconds cache TTL (backend de 10s cache yapıyor)

export function usePlanUsage() {
  const userId = useAuthStore((s) => s.session?.user.id);
  const [usage, setUsage] = useState<PlanUsage | null>(cachedUsage);
  const [isLoading, setIsLoading] = useState(!cachedUsage);
  const [error, setError] = useState<string | null>(null);

  const fetchUsage = useCallback(async (force = false) => {
    if (!userId) return;

    const now = Date.now();
    if (!force && cachedUsage && (now - cacheTimestamp < CACHE_DURATION_MS)) {
      setUsage(cachedUsage);
      setIsLoading(false);
      return;
    }

    if (activePromise) {
      try {
        const data = await activePromise;
        setUsage(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Hata oluştu.');
      } finally {
        setIsLoading(false);
      }
      return;
    }

    activePromise = (async () => {
      const token = await authService.getAccessToken();
      const res = await fetch(`${API_BASE}/api/billing/my-usage`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Kullanım verisi alınamadı.');
      const data = await res.json();
      cachedUsage = data;
      cacheTimestamp = Date.now();
      return data;
    })();

const DEFAULT_FALLBACK_USAGE: PlanUsage = {
  lessonsUsed: 0,
  lessonsLimit: 3,
  aiMessagesUsed: 0,
  aiMessagesLimit: 15,
  projectsUsed: 0,
  projectsLimit: 2,
  codeEvaluationsUsed: 0,
  codeEvaluationsLimit: 5,
  gamesUsed: 0,
  gamesLimit: 3,
  quizzesGeneratedUsed: 0,
  quizzesGeneratedLimit: 3,
  planName: 'Ücretsiz',
  planTier: 'free',
};

    try {
      const data = await activePromise;
      setUsage(data);
      setError(null);
    } catch (err) {
      console.warn('[usePlanUsage] Kullanım verisi alınamadı, güvenli fallback devrede:', err);
      setError(err instanceof Error ? err.message : 'Kullanım verisi alınamadı.');
      // Uygulamanın kilitlenmesini engellemek için varsayılan ücretsiz plan limitlerini sun
      if (!cachedUsage) {
        setUsage(DEFAULT_FALLBACK_USAGE);
      }
      // Hataları önbelleğe alma, bir sonraki sefere tekrar denensin
      cacheTimestamp = 0;
    } finally {
      activePromise = null;
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  const refetch = useCallback(() => {
    // Taze bir istek yapılması için önbelleği sıfırla
    cachedUsage = null;
    cacheTimestamp = 0;
    activePromise = null;
    return fetchUsage(true);
  }, [fetchUsage]);

  const getLimitAndUsage = (type: LimitType) => {
    if (!usage) return { used: 0, limit: null };
    switch (type) {
      case 'lesson':
        return { used: usage.lessonsUsed, limit: usage.lessonsLimit };
      case 'ai_message':
        return { used: usage.aiMessagesUsed, limit: usage.aiMessagesLimit };
      case 'project':
        return { used: usage.projectsUsed, limit: usage.projectsLimit };
      case 'code_evaluation':
        return { used: usage.codeEvaluationsUsed ?? 0, limit: usage.codeEvaluationsLimit ?? null };
      case 'game_play':
        return { used: usage.gamesUsed ?? 0, limit: usage.gamesLimit ?? null };
      case 'quiz_generation':
        return { used: usage.quizzesGeneratedUsed ?? 0, limit: usage.quizzesGeneratedLimit ?? null };
      case 'quiz_solve':
        return { used: usage.quizzesSolvedUsed ?? 0, limit: usage.quizzesSolvedLimit ?? null };
      default:
        return { used: 0, limit: null };
    }
  };

  const isAtLimit = (type: LimitType): boolean => {
    const { used, limit } = getLimitAndUsage(type);
    return limit !== null && used >= limit;
  };

  const isNearLimit = (type: LimitType): boolean => {
    const { used, limit } = getLimitAndUsage(type);
    return limit !== null && used >= limit * 0.8 && used < limit;
  };

  return { usage, isLoading, error, refetch, isAtLimit, isNearLimit, getLimitAndUsage };
}
